import { css } from '@emotion/core';
import moment from 'moment';
import t from 'react-translate';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { difference, find, isEmpty, map, values } from 'underscore';
import uuid from 'react-uuid';
import {
  ChartData, ChartOptions, ChartTypeRegistry,
  TooltipItem, TooltipModel,
} from 'chart.js';

// Schemas
import {
  PracticeRoomTab, SubmissionTab, RateOfSpeech, PracticeSubmissionComment,
} from 'redux/schemas/models/video-practice';
import {
  DeleteCommentParams,
  Post, PostCommentParams, PracticeSubmissionActionParams, SkillsFeedbackRequest, UpdateCommentParams,
} from 'redux/schemas/api/video-practice';
import { SkillRating, SkillTag } from 'redux/schemas/models/skill-tag';
import { AlertMessageType } from 'redux/schemas/app/alert-message';

import {
  getVideoPracticeSubmissionLikers,
  likeVideoPracticeSubmission,
  postComment,
  undoLikeVideoPracticeSubmission,
  likeComment,
  undoLikeComment,
  getCommentLikers,
  deleteComment,
  updateComment,
  setFilteredComments,
  postVideoHasViewed,
  getMentionableUsers,
  getSummary,
  getAllFeedback,
  deleteFeedback,
} from 'redux/actions/video-practice';
import { setShowFeedbackInstruction, updateActivityPoints } from 'redux/actions/video-practice-feedback';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { addAlertMessage } from 'redux/actions/alert-messages';

// Selectors
import {
  getBadges, getScenario, getSubmission, getSubmissionComments,
  getMentionableUsersForSubmission, authorRatingsSorted,
  getSubmissionRatingsByUser, getSkillTag,
} from 'redux/selectors/video-practice';
import { getCurrentUserId } from 'redux/selectors/timeline';
import { getCurrentUser } from 'redux/selectors/users';

// Hooks
import { AngularContext, AngularServicesContext } from 'react-app';
import { NovoEdFile } from 'shared/hooks/use-upload-file';

// Styles
import { gray1, gray4, gray5, gray7, primary, warning } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
  threeQuartersSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';

// Components
import NvCommentRow from 'shared/components/nv-comment-row';
import NvIcon from 'shared/components/nv-icon';
import NvVideoPreview, { NvVideoPreviewImperativeInterface } from 'shared/components/nv-video-preview';
import NvLikeButton from 'shared/components/nv-like-button';
import NvCommentButton from 'practice_room/components/shared/nv-comment-button';
import LoadingWrapper, { LoaderType } from 'shared/components/loading-wrapper';
import NvCommentForm from 'shared/components/nv-comment-form';
import { Badge } from 'shared/components/nv-seekbar-badges';
import ClickableContainer from 'components/clickable-container';
import NvTooltip from 'shared/components/nv-tooltip';
import { NvResponsiveTabsDisplayType, NvTab } from 'shared/components/nv-responsive-tabs';
import NvResponsiveTabsRow from 'shared/components/nv-responsive-tabs-row';
import NvStarRating from 'shared/components/nv-star-rating';
import ResponsivelyEmbeddedAngularHTML from 'shared/components/responsively-embedded-angular-html';
import NvUserAvatar from 'components/nv-user-avatar';
import { LoadingSpinnerType } from 'shared/components/loading-spinner';
import NvChart from 'shared/components/nv-chart';

import RecordVideoComment from '../submission/record-video-comment';
import PracticeSubmissionOptions from './practice-submission-options';
import { config } from '../../../../config/pendo.config.json';
import { config as configConstants } from '../../../../config/config.json';
import SkillFeedback from '../skill-feedback/skill-feedback';
import RatingSummary from '../skill-feedback/rating-summary';
import ViewFeedback from '../skill-feedback/view-feedback';
import { FeedbackInstructionModal, FeedbackInstructionPopover } from './feedback-instruction';

type VideoPracticeSubmissionProps = {
  submissionId: number
  // *2nd* Practice Recorded on 12/18/2020 4:40 PM
  index?: number
  skillTags?: SkillTag[]
  showSubmissionOptions?: boolean
  isPracticeFeedback?: boolean
  scenarioId?: number
};

type Tab = NvTab & {
  tabValue: SubmissionTab
};

const getChartData = (rateOfSpeech: RateOfSpeech[]): {
  data: ChartData, options: ChartOptions
} => {
  const labels = []; const wpm = [];
  map(rateOfSpeech, value => {
    /**
     * 0 -
     * 30 s
     * To display x axis ticks labels in multiple line
     */
    const label = [`${value.start} -`, `${value.end} s`];
    labels.push(label);
    wpm.push(value.wordsPerMinute);
  });
  const data: ChartData = {
    labels,
    datasets: [{
      label: 'WPM',
      data: wpm,
      fill: false,
      borderColor: primary,
      tension: 0,
      borderWidth: 1,
    }],
  };

  const options: ChartOptions = {
    scales: {
      y: {
        suggestedMin: 90,
        suggestedMax: 180,
        grid: { display: false, drawBorder: false },
        ticks: {
          // To add suffix or prefix with tick value
          callback(value, index, ticks) {
            return `${value} WPM`;
          },
          font: {
            size: 10,
            family: 'inherit',
          },
          autoSkip: true,
          color: gray1,
          align: 'inner',
        },
      },
      x: {
        grid: { display: false, borderColor: gray4, borderWidth: 1.5 },
        ticks: {
          color: gray1,
          font: {
            size: 10,
            family: 'inherit',
          },
          align: 'inner',
        },
      },
    },
    borderColor: gray4,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label(this: TooltipModel<keyof ChartTypeRegistry>,
            item: TooltipItem<keyof ChartTypeRegistry>) {
            const label = `${item.formattedValue} WPM`;
            return label;
          },
          title(tooltipItems: TooltipItem<keyof ChartTypeRegistry>[]) {
            const title = tooltipItems[0].label.replace(/[ ,]/g, '');
            return title;
          },
        },
      },
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            yMin: configConstants.recordings.wpm.fast,
            yMax: configConstants.recordings.wpm.fast,
            borderDash: [5, 5],
            borderColor: gray5,
          },
          line2: {
            type: 'line',
            yMin: configConstants.recordings.wpm.slow,
            yMax: configConstants.recordings.wpm.slow,
            borderDash: [5, 5],
            borderColor: gray5,
          },
          label1: {
            type: 'label',
            xValue() {
              if (wpm.length - 1 > 0) { return wpm.length - 1; }
              return 1;
            },
            yValue: configConstants.recordings.wpm.fast,
            content: t.PRACTICE_ROOM.INSIGHTS.WPM.FAST(),
            font: {
              size: 12,
              family: 'inherit',
            },
            color: gray4,
            position: 'end',
          },
          label2: {
            type: 'label',
            xValue() {
              if (wpm.length - 1 > 0) { return wpm.length - 1; }
              return 1;
            },
            yValue: configConstants.recordings.wpm.slow,
            content: t.PRACTICE_ROOM.INSIGHTS.WPM.SLOW(),
            font: {
              size: 12,
              family: 'inherit',
            },
            color: gray4,
            position: 'end',
          },
        },
      },
    },
    elements: {
      point: {
        radius: 5,
        backgroundColor: primary,
      },
    },
  };

  return { data, options };
};
export const GET_FEEDBACK_PAGE_SIZE = 5;

const bgGradient = 'linear-gradient(88.8deg, #1E9FD6 2.06%, #6597FF 97.94%)';

const rtlBgGradient = 'linear-gradient(-88.8deg, #1E9FD6 2.06%, #6597FF 97.94%)';

const styles = (isMyPractice: boolean) => css`
  .social-actions {
    height: ${tripleSpacing}px;
    background-color: ${gray7};
    .action-item {
      .icon {
        ${isMyPractice && 'pointer-events: none;'}
        margin-right: ${quarterSpacing}px;
        html[dir="rtl"] & {
          margin-left: ${quarterSpacing}px;
        }
      }
      .likes {
        cursor: pointer;
      }
    }
  }

  .featured {
    width: ${doubleSpacing}px;
    height: ${standardSpacing}px;
    background-color: ${warning};
    right: -${quarterSpacing}px;
    top:-${quarterSpacing}px;
    z-index: 1;
    &:after {
      content: '';
      width: 0;
      height: 0;
      background: transparent;
      position: absolute;
      bottom: -${quarterSpacing}px;
      right: 0;
      border-style: solid;
      border-color: transparent transparent transparent ${gray4};
      border-width: 0 0 ${quarterSpacing}px ${quarterSpacing}px;

      html[dir="rtl"] & {
        border-color: ${gray4} transparent transparent transparent;
        border-width: ${quarterSpacing}px 0px 0px ${quarterSpacing}px;
      }
    }
  }

  .player-container {
    .comment, .comment-and-feedback {
      position: absolute;
      bottom: 5%;
    }

    .jwplayer-container {
      .nv-seekbar-badge-wrapper {
        width: 100%;
        display: flex;
        margin: 0 12px;
        position: relative;

        .nv-seekbar-badge {
          position: absolute;
          top: -${standardSpacing}px;
          transform: translateX(-${halfSpacing}px);
          cursor: pointer;

          &.highlighted {
            top: -${largeSpacing}px;
            transform: translateX(-${threeQuartersSpacing}px);
          }
        }
      }
    }
  }

  .jwplayer-container {
    .jwplayer:not(.jw-state-playing) {
      .jw-media {
        opacity: 0.5;
      }
    }
  }

  .view-more {
    cursor: pointer;
  }

  .submission-tabs-row {
    .tab {
      height: auto;
      min-height: ${tripleSpacing}px;
      padding-top: ${halfSpacing}px;
      padding-bottom: ${halfSpacing}px;
    }
  }

  .submission-action-option {
    .icon {
      margin-top: 2px;
    }
  }

  .skill-feedback {
    display: flex;
    justify-content: flex-end;
    height: ${tripleSpacing}px;
    width: ${tripleSpacing}px;
    border-radius: ${largeSpacing}px;
    background: ${bgGradient};
    cursor: pointer;
    box-shadow: 0px ${quarterSpacing}px ${standardSpacing}px rgba(0, 154, 192, 0.3), 0px ${quarterSpacing}px ${standardSpacing}px rgba(29, 33, 38, 0.2);

    html[dir="rtl"] & {
      background: ${rtlBgGradient};
    }
  }
  .tag-badge {
    border-radius: ${standardSpacing}px;
  }
  .name-container .first-name, .last-name {
    font-size: 12px;
  }
  .own-feedback-container {
    .own-feedback {
      opacity: 0;
    }
    &:hover, &:focus-within {
      .own-feedback {
        opacity: 1;
      }
    }
  }
  .insights-loader {
    svg {
      width: 100px;
      height: 100px;
    }
  }
  .insight-count {
    height: 140px;
    min-width: 140px;
  }
  .phrase {
    width: fit-content;
    border-radius: ${threeQuartersSpacing}px;
    padding: 3px ${halfSpacing}px;
  }

  .feedback-by-user {
    button {
      background: none;
      border: none;
      color: ${primary};
    }

    .feeback-ratings {
      display: flex;
      margin-top: ${standardSpacing}px;

      .skill-ratings {
        width: 100%;

        .feedback-date {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
`;

function selectedViewToIndex(selectedView: SubmissionTab) {
  switch (selectedView) {
    case SubmissionTab.COMMENTS:
      return 0;
    case SubmissionTab.AUTHOR_FEEDBACK:
      return 1;
    case SubmissionTab.ALL_FEEDBACK:
      return 2;
    case SubmissionTab.INSIGHTS:
      return 3;
    default:
      return 0;
  }
}

const INITIAL_COMMENTS_PER_PAGE = 5;

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
type InsightsCountProps = {
  count: number,
  label: string,
  className: string,
  showCount?: boolean,
};
const InsightsCount = ({
  count,
  label,
  className,
  showCount = true,
}: InsightsCountProps) => (
  <div
    className={`insight-count d-flex flex-column bg-gray-7 align-items-center justify-content-center ${className}`}
  >
    <div className='course-title-l'>{showCount ? count : 'N/A'}</div>
    <div className='text-small font-weight-bolder text-center'>
      {label}
    </div>
  </div>
);

const PhraseList = (props: { label: string, items: string[], borderClassName: string }) => (
  <div>
    <div className='gray-1 mb-2 mt-4'>
      {props.label}
    </div>
    <div className='d-flex flex-wrap'>
      {props.items.map(phrase => (
        <div
          key={uuid()}
          className={`border phrase align-items-center label gray-1 mr-2 bg-gray-7 mb-2 ${props.borderClassName}`}
        >
          {phrase}
        </div>
      ))}
    </div>
  </div>
);
type DisplaySkillRatingProps = {
  skillRating: SkillRating;
};

const DisplaySkillRating = ({ skillRating }: DisplaySkillRatingProps) => {
  const angularServices = useContext(AngularServicesContext);
  const skillTag = useSelector(state => getSkillTag(state, skillRating?.skillTaggingId));

  return (
    <div className='d-flex flex-column w-100 ml-2'>
      <div className='d-flex justify-content-between mb-2'>
        <div className='d-flex'>
          <div className='text-medium font-weight-bolder gray-2 mr-2'>{skillTag?.name}</div>
          <NvStarRating readonly ratedValue={skillRating?.rating} starSize='smallest' />
        </div>
      </div>
      <ResponsivelyEmbeddedAngularHTML
        className='text-body'
        angularServices={angularServices}
        template={skillRating?.body}
      />
    </div>
  );
};

const PracticeSubmission = ({
  submissionId,
  index: practiceIndex,
  skillTags,
  showSubmissionOptions = true,
  isPracticeFeedback,
  ...restProps
}: VideoPracticeSubmissionProps) => {
  const { $uibModal, TimelinesManager } = useContext(AngularServicesContext);
  const { $scope } = useContext(AngularContext);

  const scenarioParam = useSelector((state) => state.app.practiceRoom.params.scenarioId);
  const scenarioId = scenarioParam ?? restProps.scenarioId;
  const practicesubmission = useSelector((state) => getSubmission(state, submissionId));
  const currentUserId = useSelector(getCurrentUserId);
  const {
    activity,
    isLikedByCurrentUser,
    likedUsers,
    submissionComments,
    featured,
    videoFile,
    numComments,
    numViews,
    numLikes,
    user,
    completedAt: recordedDate,
    isRatedByCurrentUser,
    isViewerMentor,
    coveredKeyphrases,
    notAvoidedKeyphrases,
    rateOfSpeech,
    wordsPerMinute,
    transcriptionCompleted,
    hasTranscription,
    keyphrasesToCover,
    keyphrasesToAvoid,
    userPracticeNumber,
    hasReviewed,
  } = practicesubmission;
  const {
    isPracticeAdmin, myPracticeVisitedAt,
    hasCourseAdmin, insightCriterion,
  } = useSelector((state) => getScenario(state, scenarioId));
  const badges = useSelector((state) => getBadges(state, submissionId));
  const filteredComments = useSelector((state) => state.app.practiceRoom.filteredComments?.[submissionId]);
  const { newNotification, userInteractedWith } = useSelector((state) => state.app.practiceRoom.params);
  const {
    commentsOnceLoaded,
    feedbackOnceLoaded,
    summary,
    summaryOnceLoaded,
    authorFeedback,
    feedbackLoading,
    feedbackByUserOnceLoaded,
    ratingsByUser,
    mentionables,
  } = useSelector((state) => state.app.videoPracticeSubmissions[submissionId]) ?? {};
  const currentInstitutionId = useSelector(
    (state) => state.app.currentInstitutionId,
  );
  const authorRatings = useSelector(state => authorRatingsSorted(state, submissionId));

  const comments = useSelector((state) => getSubmissionComments(state, submissionComments));
  const notificationComment = useSelector((state) => state.models.comments?.[newNotification?.commentId]) ?? null;
  const mentionableUsers = useSelector((state) => getMentionableUsersForSubmission(state, { submissionId }));
  const practiceSubmissionRef = useRef(null);
  const videoRef = useRef(null);
  const feedbackByUserRef = useRef(null);
  const {
    submissionId: selectedSubmissionId,
    selectedTab,
    userId: currentUser,
    selectedSubmissionTab,
    feedbackBy,
  } = useSelector((state) => state.app.practiceRoom.params);
  const ratingIds = (ratingsByUser && feedbackBy && ratingsByUser[feedbackBy]) ? ratingsByUser[feedbackBy] : [];
  const skillsFeedbackByUser = useSelector((state) => getSubmissionRatingsByUser(state, ratingIds));
  const { feedbackCriteriaId, showFeedbackInstruction } = useSelector(state => state.app.videoPracticeFeedback);
  const practiceFeedbackActivity = useSelector(state => state.models.practiceFeedbackActivities?.[feedbackCriteriaId]);
  const currentLectureId = useSelector(state => state.app.lecturePage?.currentLectureId);
  const loggedInUser = useSelector(getCurrentUser);
  const isMyPractice = user?.id === currentUserId;

  const dispatch = useAppDispatch();

  const [viewMore, setViewMore] = useState(false);
  const [showCommentButton, setShowCommentButton] = useState(true);
  const [showRecordVideoComment, setShowRecordVideoComment] = useState(false);
  const [showCommentInputRow, setShowCommentInputRow] = useState(false);
  const [currentTimestamp, setCurrentTimestamp] = useState(null);
  const [isNotifiedCommentsSet, setIsNotifiedComments] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [selectedView, setSelectedView] = useState<SubmissionTab>(SubmissionTab.COMMENTS);
  const [showFeedbackByUser, setShowFeedbackByUser] = useState(Boolean(feedbackBy && (submissionId === selectedSubmissionId)));
  const [showInstructionPopover, setShowInstructionPopover] = useState(false);
  const [isCommentButtonExpanded, setCommentButtonExpanded] = useState(showFeedbackInstruction && isPracticeFeedback);
  const [showFeedbackInstructionModal, setFeedbackInstructionModal] = useState(false);
  const [practiceFeedbackData, setPracticeFeedbackData] = useState<PracticeSubmissionComment>();

  const prevUser = usePrevious(currentUser);

  let likeIconClass = isLikedByCurrentUser ? 'text-primary' : 'text-gray-3';
  if (isMyPractice) {
    likeIconClass = 'text-gray-5';
  }

  const playerInstanceRef = useRef<NvVideoPreviewImperativeInterface>(null);

  useEffect(() => {
    if (submissionId === selectedSubmissionId && selectedSubmissionTab) {
      setSelectedView(selectedSubmissionTab);
    }
  }, [selectedSubmissionId, selectedSubmissionTab, submissionId]);

  // If this is same submission selected (submissionId is in url), then try
  // to scroll to it
  useEffect(() => {
    if (commentsOnceLoaded
      && selectedSubmissionId
      && selectedSubmissionId === submissionId) {
      if (prevUser === currentUser && videoRef?.current) {
        videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTimeout(() => {
          if (videoRef?.current) {
            videoRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          // A timeout of 500 millisec to finish the transition (if any) before scrolling
        }, 500);
      }


      if (playerInstanceRef.current && userInteractedWith) {
        playerInstanceRef.current.play(true);
      }
    }
  }, [commentsOnceLoaded, selectedSubmissionId, submissionId, prevUser, currentUser, userInteractedWith]);

  useEffect(() => {
    setTimeout(() => {
      if (feedbackByUserRef?.current && feedbackBy && skillsFeedbackByUser.length > 0) {
        feedbackByUserRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 1000);
  }, [feedbackByUserRef, feedbackBy, skillsFeedbackByUser]);

  useEffect(() => {
    if (!isNotifiedCommentsSet && comments?.length && newNotification?.submissionId && newNotification?.commentId && notificationComment) {
      const commentsNewerThanNotification = comments.filter(comment => moment(comment.createdAt).isSameOrAfter(moment(notificationComment.createdAt)));
      const ids = commentsNewerThanNotification.map(comment => comment.id);
      setIsNotifiedComments(true);
      if (ids.length) {
        dispatch(setFilteredComments({
          submissionId: newNotification.submissionId,
          filteredComments: ids,
        }));
      }
    }
  }, [dispatch, comments, newNotification, notificationComment, isNotifiedCommentsSet, setIsNotifiedComments]);

  const onLike = useCallback(() => {
    if (scenarioId && submissionId) {
      const params: PracticeSubmissionActionParams = { scenarioId, submissionId };
      if (isPracticeFeedback) {
        params.feedbackCriteriaId = feedbackCriteriaId;
      }
      dispatch(likeVideoPracticeSubmission(params));
    }
  }, [dispatch, feedbackCriteriaId, isPracticeFeedback, scenarioId, submissionId]);

  const onUndoLike = useCallback(() => {
    if (scenarioId && submissionId) {
      const params: PracticeSubmissionActionParams = { scenarioId, submissionId };
      if (isPracticeFeedback) {
        params.feedbackCriteriaId = feedbackCriteriaId;
      }
      dispatch(undoLikeVideoPracticeSubmission(params));
    }
  }, [dispatch, feedbackCriteriaId, isPracticeFeedback, scenarioId, submissionId]);

  const onGetPracticeSubmissionLikers = useCallback(() => {
    if (scenarioId && submissionId) {
      dispatch(getVideoPracticeSubmissionLikers({ scenarioId, submissionId }));
    }
  }, [dispatch, scenarioId, submissionId]);

  const onGetSkillsFeedback = useCallback((
    skillTaggingId?: number,
    afterId?: number,
  ) => {
    if (submissionId) {
      if (selectedView === SubmissionTab.AUTHOR_FEEDBACK || skillTaggingId) {
        const params: SkillsFeedbackRequest = {
          institutionId: currentInstitutionId,
          ownerId: submissionId,
          ownerType: 'VideoPracticeSubmission',
          skillTaggingId,
          displayOwnFeedback: selectedView === SubmissionTab.AUTHOR_FEEDBACK,
        };
        if (skillTaggingId) {
          params.pageSize = GET_FEEDBACK_PAGE_SIZE;
          params.afterId = afterId ?? null;
        }
        dispatch(getAllFeedback(params));
      } else if (selectedView === SubmissionTab.ALL_FEEDBACK) {
        dispatch(getSummary({
          institutionId: currentInstitutionId,
          ownerId: submissionId,
          ownerType: 'VideoPracticeSubmission',
        }));
      }
    }
  }, [currentInstitutionId, dispatch, selectedView, submissionId]);

  const getFeedbackByUser = useCallback(() => {
    const params: SkillsFeedbackRequest = {
      institutionId: currentInstitutionId,
      ownerId: submissionId,
      ownerType: 'VideoPracticeSubmission',
      displayOwnFeedback: true,
      userId: feedbackBy,
    };

    dispatch(getAllFeedback(params));
  }, [
    dispatch,
    currentInstitutionId,
    submissionId,
    feedbackBy,
  ]);

  useEffect(() => {
    if (submissionId && showFeedbackByUser && selectedView === SubmissionTab.ALL_FEEDBACK) {
      getFeedbackByUser();
    }
  }, [getFeedbackByUser, submissionId, showFeedbackByUser, selectedView]);

  useEffect(() => {
    if (selectedView === SubmissionTab.ALL_FEEDBACK && !showFeedbackByUser) {
      onGetSkillsFeedback();
    }
  }, [onGetSkillsFeedback, selectedView, showFeedbackByUser]);

  useEffect(() => {
    if (isRatedByCurrentUser
      && selectedView === SubmissionTab.AUTHOR_FEEDBACK
      && !authorFeedback?.ratings?.length
      && !feedbackLoading) {
      onGetSkillsFeedback();
    }
  }, [
    authorFeedback?.ratings?.length, feedbackLoading,
    isRatedByCurrentUser, onGetSkillsFeedback, selectedView,
  ]);

  useEffect(() => {
    // close popover when comment button collapsed.
    // also  hide popover for an activity once it is displayed.
    if (!isCommentButtonExpanded) {
      setShowInstructionPopover(false);
      if (showFeedbackInstruction) {
        dispatch(setShowFeedbackInstruction({ show: false, feedbackCriteriaId }));
      }
    }
  }, [dispatch, feedbackCriteriaId, isCommentButtonExpanded, showFeedbackInstruction]);

  const onReady = useCallback(() => {
    // show popover initially after a delay to position popover in place
    if (isCommentButtonExpanded && showFeedbackInstruction) {
      setShowInstructionPopover(true);
    }
    if (!showFeedbackInstruction) {
      setShowInstructionPopover(false);
    }
  }, [isCommentButtonExpanded, showFeedbackInstruction]);

  useEffect(() => {
    // close comment button if feedback instruction is false for an activity (eg: while sliding )
    if (!showFeedbackInstruction) {
      setCommentButtonExpanded(false);
    }
  }, [showFeedbackInstruction, submissionId]);

  const pauseVideo = useCallback(() => {
    // Pause the player and get current time stamp
    if (playerInstanceRef.current) {
      const playerState = playerInstanceRef.current.getState();

      if (playerState === 'playing' || playerState === 'paused') {
        if (playerState === 'playing') {
          // Pause the player
          playerInstanceRef.current.play(false);
        }

        const currentTime = playerInstanceRef.current.getPosition();
        if (currentTime >= 1) {
          setCurrentTimestamp(currentTime);
        } else {
          setCurrentTimestamp(null);
        }
      } else {
        setCurrentTimestamp(null);
      }
    }
  }, []);

  const onComment = useCallback(() => {
    if (showFeedbackInstruction) {
      dispatch(setShowFeedbackInstruction({ show: false }));
    }
    if (playerInstanceRef.current.getState() === 'playing'
      || playerInstanceRef.current.getState() === 'paused') {
      pauseVideo();
    }
    setShowCommentButton(false);
    setCommentButtonExpanded(false);
  }, [dispatch, pauseVideo, showFeedbackInstruction]);

  const onTextComment = useCallback(() => {
    onComment();
    setShowCommentInputRow(true);
  }, [onComment]);

  const onVideoComment = useCallback(() => {
    onComment();
    setShowRecordVideoComment(true);
  }, [onComment]);

  const onCloseRecordVideoComment = useCallback((file?: NovoEdFile) => {
    setShowRecordVideoComment(false);
    setShowCommentInputRow(false);
    setShowCommentButton(true);
  }, []);

  const afterSubmit = useCallback(() => {
    setShowCommentInputRow(false);
    setShowRecordVideoComment(false);
    setShowCommentButton(true);
    if (selectedView !== SubmissionTab.COMMENTS) {
      setSelectedView(SubmissionTab.COMMENTS);
    }
  }, [selectedView]);

  useEffect(() => {
    if (practiceFeedbackData) {
      const {
        pointsReceived,
        leaderboardPoints,
        leaderboardRank,
        priorLeaderboardRank,
      } = practiceFeedbackData;

      if (isPracticeFeedback && practiceFeedbackActivity?.progress !== 'missed') {
        if (practiceFeedbackActivity?.numReviewesCompleted === practiceFeedbackActivity?.requiredToComplete) {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.ACTIVITY_COMPLETED(
              practiceFeedbackActivity.requiredToComplete,
            ),
          }));
          // Update points and progress of timeline after activity has been submitted
          // Update completed
          TimelinesManager.updateComponentPointsAndProgress(
            practiceFeedbackActivity.lecturePageId,
            'feedback_criteria',
            practiceFeedbackActivity.id,
            pointsReceived,
            leaderboardPoints,
            'completed',
          );
        } else {
          dispatch(addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.MODAL.SUCCESS_MSG(),
          }));
          // In case of uncompleted activity update progress state if not points received
          if (practiceFeedbackActivity?.progress !== 'completed') {
            if (pointsReceived) {
              TimelinesManager.updateComponentPointsAndProgress(
                practiceFeedbackActivity.lecturePageId,
                'feedback_criteria',
                practiceFeedbackActivity.id,
                pointsReceived,
                leaderboardPoints,
                'in_progress',
              );
            } else {
              TimelinesManager.updateComponentProgress(
                practiceFeedbackActivity.lecturePageId,
                'feedback_criteria',
                practiceFeedbackActivity.id,
                'in_progress',
              );
            }
          }
        }
        const currentPointsReceived = practiceFeedbackActivity?.pointsConfiguration?.points;

        if (pointsReceived && pointsReceived > practiceFeedbackActivity?.pointsReceived) {
          $uibModal.open({
            templateUrl: 'shared/templates/points-modal.html',
            windowClass: 'points-modal',
            controller: 'PointsModalCtrl as vm',
            resolve: {
              pointsReceived: currentPointsReceived,
              leaderboardPoints,
              leaderboardRank,
              priorLeaderboardRank,
              extras: null,
            },
          });
        }
      }
      setPracticeFeedbackData(null);
    }
  }, [practiceFeedbackActivity?.numReviewesCompleted, practiceFeedbackData,
    isPracticeFeedback, practiceFeedbackActivity?.progress,
    practiceFeedbackActivity?.pointsReceived, hasReviewed, $uibModal,
  ]);

  const addNewComment = useCallback(async (comment: Post) => {
    const data: PostCommentParams = {
      post: comment,
      submissionId,
    };
    if (isPracticeFeedback && feedbackCriteriaId) {
      data.feedbackCriteriaId = feedbackCriteriaId;
    }
    const { payload } = await dispatch(postComment(data));
    if (!payload) {
      return false;
    }
    if (isPracticeFeedback && payload.isUserFirstReview) {
      setPracticeFeedbackData(payload);
    }
    afterSubmit();
    return true;
  }, [afterSubmit, dispatch, feedbackCriteriaId, isPracticeFeedback, submissionId]);

  const onUpdateComment = useCallback(async (commentId: number, comment: Post) => {
    const data: UpdateCommentParams = { commentId, post: comment };
    if (isPracticeFeedback) {
      data.feedbackCriteriaId = feedbackCriteriaId;
    }
    const { payload } = await dispatch(updateComment(data));
    if (!payload) {
      return false;
    }
    return true;
  }, [dispatch, feedbackCriteriaId, isPracticeFeedback]);

  const onDeleteComment = useCallback(async (commentId: number, isUserFirstReview?: boolean) => {
    const params: DeleteCommentParams = { commentId, submissionId, isUserFirstReview };

    if (isPracticeFeedback) {
      params.feedbackCriteriaId = feedbackCriteriaId;
    }
    const { payload } = await dispatch(deleteComment(params));

    if (isUserFirstReview && payload?.publicFeedback) {
      TimelinesManager.updateComponentPointsAndProgress(
        practiceFeedbackActivity?.lecturePageId ?? currentLectureId,
        'feedback_criteria',
        practiceFeedbackActivity?.id ?? payload.publicFeedback.id,
        payload.publicFeedback.pointsReceived,
        payload.leaderboardPoints,
        payload.publicFeedback.progress,
      );
      // To trigger a render in the angular component so that points update immediately
      $scope.$apply();
    }
  }, [dispatch, feedbackCriteriaId, isPracticeFeedback, submissionId]);

  const filterCommentsOfTime = useCallback((badge: Badge) => {
    if (playerInstanceRef.current) {
      playerInstanceRef.current.play(false);
      if (badge.meta.time) {
        // Seek to two seconds before
        const seekTime = badge.meta.time > 2 ? (badge.meta.time - 2) : 0;

        playerInstanceRef.current.seek(seekTime);
      }
    }

    dispatch(setFilteredComments({
      submissionId,
      filteredComments: badge.meta.extras.commentIds,
    }));
  }, [dispatch, submissionId]);

  const clearFilterComments = useCallback(() => {
    dispatch(setFilteredComments({
      submissionId,
      filteredComments: [],
    }));
  }, [dispatch, submissionId]);

  const onTimestampClick = useCallback((commentId: number) => {
    const badge = find(badges, (b) => b.meta.extras.commentIds.includes(commentId));
    if (badge) {
      filterCommentsOfTime(badge);
    }
  }, [badges, filterCommentsOfTime]);

  const onPlayVideo = useCallback((event) => {
    if (playerInstanceRef.current && !playerInstanceRef.current.hasPlayed && !isMyPractice) {
      dispatch(postVideoHasViewed({ scenarioId, submissionId }));
    }
  }, [dispatch, isMyPractice, scenarioId, submissionId]);

  const loadMentionableUsers = useCallback(() => {
    if (!mentionables?.isLoading && !mentionables?.onceLoaded) {
      dispatch(getMentionableUsers({ scenarioId, submissionId }));
    }
  }, [mentionables?.isLoading, mentionables?.onceLoaded, dispatch, scenarioId, submissionId]);

  const deleteSkillsFeedback = useCallback((userId) => {
    dispatch(deleteFeedback({
      institutionId: currentInstitutionId,
      ownerId: submissionId,
      ownerType: 'VideoPracticeSubmission',
      userId,
    }));
  }, [currentInstitutionId, dispatch, submissionId]);

  const onDeleteFeedback = (userId?: number) => {
    const warningTitle = userId
      ? t.PRACTICE_ROOM.SKILL_FEEDBACK.DELETE_AS_ADMIN.TITLE()
      : t.PRACTICE_ROOM.SKILL_FEEDBACK.DELETE_AS_AUTHOR();
    const desc = userId ? t.PRACTICE_ROOM.SKILL_FEEDBACK.DELETE_AS_ADMIN.DESCRIPTION() : '';
    dispatch(openConfirmationDialog({
      onConfirm: () => deleteSkillsFeedback(userId ?? currentUserId),
      cancelText: t.FORM.CANCEL(),
      confirmText: t.FORM.YES_SURE(),
      title: warningTitle,
      bodyText: desc,
    }));
  };

  const toggleShowFeedbackByUser = () => {
    setShowFeedbackByUser(displayFeedbackByUser => !displayFeedbackByUser);
  };

  const tabs: Tab[] = [
    {
      text: t.PRACTICE_ROOM.SUBMISSION.TABS.COMMENTS(),
      onClick: () => setSelectedView(SubmissionTab.COMMENTS),
      pendoTagName: config.pendo.skillsFeedback.practiceTabComments,
      tabValue: SubmissionTab.COMMENTS,
    },
  ];

  if (!isPracticeFeedback) {
    if (!isMyPractice && skillTags?.length > 0) {
      tabs.push({
        text: t.PRACTICE_ROOM.SUBMISSION.TABS.YOUR_SKILLS_FEEDBACK(),
        onClick: () => setSelectedView(SubmissionTab.AUTHOR_FEEDBACK),
        pendoTagName: config.pendo.skillsFeedback.practiceTabYourSkillsFeedback,
        tabValue: SubmissionTab.AUTHOR_FEEDBACK,
      });
    }
    if (isMyPractice || hasCourseAdmin || isViewerMentor) {
      if (skillTags?.length > 0) {
        tabs.push({
          text: t.PRACTICE_ROOM.SUBMISSION.TABS.ALL_SKILLS_FEEDBACK(),
          onClick: () => setSelectedView(SubmissionTab.ALL_FEEDBACK),
          pendoTagName: config.pendo.skillsFeedback.practiceTabAllSkillsFeedback,
          tabValue: SubmissionTab.ALL_FEEDBACK,
        });
      }

      if (!isEmpty(insightCriterion) && hasTranscription) {
        tabs.push({
          text: t.PRACTICE_ROOM.SUBMISSION.TABS.INSIGHTS(),
          onClick: () => setSelectedView(SubmissionTab.INSIGHTS),
          dataQA: 'automated-feedback-insights-tab',
          pendoTagName: config.pendo.practice.insightsTab,
          tabValue: SubmissionTab.INSIGHTS,
        });
      }
    }
  }

  const getActiveTabIndex = useCallback(() => {
    const allTabs = values(SubmissionTab);
    const availableTabs = tabs.map(tab => tab.tabValue);
    const missingTabs = difference(allTabs, availableTabs);
    const tabsBeforeActiveTab = missingTabs.filter(
      tab => selectedViewToIndex(tab) < selectedViewToIndex(selectedView),
    );
    return selectedViewToIndex(selectedView) - tabsBeforeActiveTab.length;
  }, [selectedView]);


  const totalComments = submissionComments?.length;

  // Slice the last five comments to display recent five comments
  const slicingIndex = (totalComments - INITIAL_COMMENTS_PER_PAGE) >= 0
    ? (totalComments - INITIAL_COMMENTS_PER_PAGE) : 0;
  let displayComments = viewMore ? submissionComments
    : submissionComments?.slice(
      slicingIndex,
      totalComments,
    ); // To display the recent comments initially
  const commentsLeft = totalComments - displayComments?.length;
  const isCommentsFiltered = !isEmpty(filteredComments);
  if (isCommentsFiltered) {
    displayComments = submissionComments?.filter((commentId) => filteredComments.includes(commentId));
  }
  const feedbackByUserData = skillsFeedbackByUser.length ? skillsFeedbackByUser[0] : {};
  const showFeedbackButton = skillTags?.length > 0 && !isRatedByCurrentUser && !isMyPractice && !isPracticeFeedback;
  const showTabs = tabs.length > 1;
  const notCoveredKeyphrases: string[] = difference(keyphrasesToCover, coveredKeyphrases) ?? [];
  const { data, options } = getChartData(rateOfSpeech);

  return (
    <div css={styles(isMyPractice)} className='d-flex flex-column w-100' ref={practiceSubmissionRef}>
      <div className='d-flex flex-column flex-md-row justify-content-between'>
        {userPracticeNumber && (
          <div className='course-title-xs font-weight-bold'>
            {t.PRACTICE_ROOM.SUBMISSION.TITLE(
              moment(recordedDate).format('L hh:mm A'),
              userPracticeNumber,
            )}
          </div>
        )}
        <div className='d-flex justify-content-between mb-1'>
          <div className='d-flex'>
            {activity?.course?.name && (
              <React.Fragment>
                <div className='card-title-xs mr-1'>
                  {t.PRACTICE_ROOM.SUBMISSION.PRACTICED_IN()}
                </div>
                <div className='course-title-xs'>{activity.course.name}</div>
              </React.Fragment>
            )}
          </div>
          {/* PR-TODO : In Phase 2 */}
          {/* <NvIcon
            icon='share'
            size='small'
            className='text-gray-3 ml-2 mb-1'
          /> */}
          {showSubmissionOptions && (
            <PracticeSubmissionOptions
              submissionId={submissionId}
              loadMentionableUsers={loadMentionableUsers}
            />
          )}
        </div>
      </div>
      <div className='d-flex flex-column mt-1 position-relative'>
        {featured && !feedbackCriteriaId && (
          <div className='featured d-flex flex-column justify-content-center align-items-center position-absolute'>
            <NvIcon
              icon='highlight'
              size='xss-smallest'
              className='text-white'
            />
          </div>
        )}
        <div className='player-container' ref={videoRef}>
          <NvVideoPreview
            ref={playerInstanceRef}
            file={videoFile}
            badges={badges}
            onClickOnBadge={filterCommentsOfTime}
            badgePendoTag={config.pendo.practice.commentFocusFromPracticePlayer}
            onPlay={onPlayVideo}
            onReady={onReady}
          />
          <div
            className={`${showFeedbackButton ? 'comment-and-feedback' : 'comment'} d-flex`}
            dir='ltr'
          >
            {showFeedbackButton && (
              <NvTooltip
                text={t.PRACTICE_ROOM.SKILL_FEEDBACK.TOOLTIP()}
                placement='bottom'
              >
                <ClickableContainer
                  className='mx-4'
                  onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                  pendo-tag-name={config.pendo.skillsFeedback.practiceGiveSkillsFeedback}
                >
                  <div className='skill-feedback medium-large d-flex justify-content-center align-items-center text-white'>
                    <NvIcon icon='skills-feedback' size='medium' />
                  </div>
                </ClickableContainer>
              </NvTooltip>
            )}
            {showCommentButton && (
              <FeedbackInstructionPopover
                enabled={showInstructionPopover && isCommentButtonExpanded}
                show={showInstructionPopover}
                title={practiceFeedbackActivity?.title}
                setShowModal={setFeedbackInstructionModal}
              >
                <NvCommentButton
                  onTextComment={onTextComment}
                  onVideoComment={onVideoComment}
                  pauseVideo={pauseVideo}
                  isExpanded={isCommentButtonExpanded}
                  setExpanded={setCommentButtonExpanded}
                />
              </FeedbackInstructionPopover>
            )}
          </div>
        </div>
        <div className='social-actions d-flex align-items-center pl-1' dir='ltr'>
          <div
            className='action-item d-flex align-items-center m-3'
            pendo-tag-name={config.pendo.practice.likePractice}
          >
            <NvLikeButton
              isLiked={isLikedByCurrentUser}
              likedCount={numLikes}
              likedUsers={likedUsers}
              isDisabled={isMyPractice}
              iconSize='small'
              iconClassName={likeIconClass}
              textClassName='likes text-primary'
              onLike={onLike}
              onUndoLike={onUndoLike}
              showLikersInfo
              getLikersInfo={onGetPracticeSubmissionLikers}
            />
          </div>
          {numComments > 0 && (
            <div className='action-item  d-flex align-items-center m-3'>
              <NvIcon size='small' icon='comments' className='text-gray-3' />
              <span className='text-gray-2'>{numComments}</span>
            </div>
          )}
          {numViews > 0 && (
            <div className='action-item  d-flex align-items-center m-3'>
              <NvIcon size='medium' icon='view' className='text-gray-3' />
              <span className='text-gray-2'>{numViews}</span>
            </div>
          )}
        </div>
      </div>
      {showFeedbackForm && (
        <SkillFeedback
          showForm={setShowFeedbackForm}
          submissionId={submissionId}
          skillTags={skillTags}
          onSubmit={() => setSelectedView(SubmissionTab.AUTHOR_FEEDBACK)}
        />
      )}
      {showCommentInputRow && (
        <NvCommentForm
          onSubmit={addNewComment}
          onCancel={() => {
            setShowCommentInputRow(false);
            setShowRecordVideoComment(false);
            setShowCommentButton(true);
          }}
          timestamp={currentTimestamp}
          timestampInfo={t.PRACTICE_ROOM.COMMENTS.TIMESTAMP_POPOVER()}
          mentionableUsers={mentionableUsers}
          loadMentionableUsers={loadMentionableUsers}
        />
      )}
      {showRecordVideoComment && (
        <RecordVideoComment
          onClose={onCloseRecordVideoComment}
          onSubmit={addNewComment}
          timestamp={currentTimestamp}
        />
      )}
      <div className='submission-tabs-row border-bottom border-gray-6 mb-4'>
        {showTabs && (
          <NvResponsiveTabsRow
            defaultTabs={tabs}
            tabTextClass='card-title'
            tabType={NvResponsiveTabsDisplayType.TEXT_ONLY}
            revertActiveTab={getActiveTabIndex()}
          />
        )}
      </div>
      <div>
        {selectedView === SubmissionTab.COMMENTS ? (
          <LoadingWrapper
            isLoaded={commentsOnceLoaded}
            loaderType={LoaderType.PLACEHOLDER}
          >
            <React.Fragment>
              {totalComments > 0
                ? (
                  <React.Fragment>
                    {isCommentsFiltered ? (
                      (
                        <React.Fragment>
                          {totalComments > filteredComments.length && (
                            <ClickableContainer
                              className='view-more text-regular text-primary my-4'
                              onClick={clearFilterComments}
                            >
                              {t.PRACTICE_ROOM.COMMENTS.ALL_COMMENTS()}
                            </ClickableContainer>
                          )}
                        </React.Fragment>
                      )
                    ) : (
                      <React.Fragment>
                        {commentsLeft > 0 && (
                          <ClickableContainer
                            className='view-more text-regular text-primary my-4'
                            onClick={() => setViewMore(true)}
                          >
                            {t.PRACTICE_ROOM.COMMENTS.VIEW_MORE(commentsLeft)}
                          </ClickableContainer>
                        )}
                      </React.Fragment>
                    )}
                    {displayComments.map(commentId => (
                      <React.Fragment key={commentId}>
                        <NvCommentRow
                          commentId={commentId}
                          isHighlighted={filteredComments?.includes(commentId)}
                          // Filtered comments only contain one comment, so play it on load
                          playOnLoad={filteredComments?.includes(commentId) && filteredComments.length === 1}
                          onLike={() => dispatch(likeComment({ commentId, feedbackCriteriaId }))}
                          onUndoLike={() => dispatch(undoLikeComment({ commentId, feedbackCriteriaId }))}
                          getLikersInfo={() => dispatch(getCommentLikers({ commentId }))}
                          onReply={addNewComment}
                          onUpdate={(comment) => onUpdateComment(commentId, comment)}
                          canDelete={hasCourseAdmin}
                          onDelete={(isUserFirstReview) => onDeleteComment(commentId, isUserFirstReview)}
                          onTimestampClick={onTimestampClick}
                          lastVisitedAt={selectedTab === PracticeRoomTab.MY_PRACTICE ? myPracticeVisitedAt : ''}
                          mentionableUsers={mentionableUsers}
                          loadMentionableUsers={loadMentionableUsers}
                        />
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ) : (
                  <div
                    className='d-flex justify-content-center text-medium gray-2 mt-5'
                  >
                    {isMyPractice
                      ? t.PRACTICE_ROOM.COMMENTS.NO_COMMENTS.MY_PRACTICE()
                      : t.PRACTICE_ROOM.COMMENTS.NO_COMMENTS.DEFAULT()}
                  </div>
                )}
            </React.Fragment>
          </LoadingWrapper>
        ) : (
          <div className={`${showFeedbackByUser ? 'my-3' : 'my-6'}`}>
            {selectedView === SubmissionTab.AUTHOR_FEEDBACK && (
              <LoadingWrapper
                isLoaded={feedbackOnceLoaded || !isRatedByCurrentUser}
                loaderType={LoaderType.PLACEHOLDER}
              >
                <React.Fragment>
                  {authorRatings?.length > 0 ? (
                    <div className='d-flex'>
                      {loggedInUser && (
                        <NvUserAvatar
                          borderType='round'
                          size='md'
                          user={loggedInUser}
                          displayName
                          className='mx-4 mb-1 gray-2 text-small'
                          directToOrgLevelProfile
                          displayRoleBadge
                        />
                      )}
                      <div className='own-feedback-container w-100 flex-column'>
                        {authorRatings?.map(rating => (
                          <ViewFeedback key={rating} ratingId={rating} isAuthor />
                        ))}
                        <div className='d-flex justify-content-between'>
                          <ClickableContainer
                            className='own-feedback d-flex gray-3 align-items-center'
                            onClick={() => onDeleteFeedback()}
                            pendo-tag-name={config.pendo.skillsFeedback.practiceSkillsFeedbackDelete}
                          >
                            <NvIcon icon='trash' size='xss-smallest' className='ml-2 mr-1' />
                            {t.NOVOED.DELETE()}
                          </ClickableContainer>
                          <div className='text-date gray-2'>
                            {moment(authorFeedback?.createdAt).format('lll')}
                          </div>
                        </div>
                        <div className='text-small gray-3 ml-2 mt-6 '>
                          {t.PRACTICE_ROOM.SKILL_FEEDBACK.FEEDBACK_VISIBILITY.AUTHOR(user.firstName)}
                        </div>
                      </div>

                    </div>
                  ) : (
                    <React.Fragment>
                      {(!isRatedByCurrentUser || (!isMyPractice && !feedbackLoading)) && (
                        <div className='d-flex justify-content-center text-medium gray-2 mt-5'>
                          {t.PRACTICE_ROOM.SKILL_FEEDBACK.EMPTY_FEEDBACK()}
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </React.Fragment>
              </LoadingWrapper>
            )}
            {selectedView === SubmissionTab.ALL_FEEDBACK && (showFeedbackByUser && submissionId === selectedSubmissionId ? (
              <LoadingWrapper
                isLoaded={feedbackByUserOnceLoaded}
                loaderType={LoaderType.PLACEHOLDER}
              >
                <div className='feedback-by-user' ref={feedbackByUserRef}>
                  <button type='button' onClick={toggleShowFeedbackByUser}>
                    {t.PRACTICE_ROOM.SKILL_FEEDBACK.VIEW_ALL_FEEDBACK()}
                  </button>
                  <div className='feeback-ratings'>
                    <div className='rating-user'>
                      {feedbackByUserData?.user && (
                        <NvUserAvatar
                          borderType='round'
                          size='md'
                          user={feedbackByUserData.user}
                          displayName
                          className='mx-4 mb-1 gray-2 text-small'
                          directToOrgLevelProfile
                          displayRoleBadge
                        />
                      )}
                    </div>
                    <div className='skill-ratings'>
                      {skillsFeedbackByUser.map(skillRating => <DisplaySkillRating key={skillRating.id} skillRating={skillRating} />)}
                      <div className='feedback-date'>
                        {feedbackByUserData?.createdAt && (
                          <div className='text-date gray-2'>{moment(feedbackByUserData.createdAt).format('lll')}</div>
                        )}
                      </div>
                      <div className='text-small gray-3 mt-6 '>
                        {t.PRACTICE_ROOM.SKILL_FEEDBACK.FEEDBACK_VISIBILITY.ALL()}
                      </div>
                    </div>
                  </div>
                </div>
              </LoadingWrapper>
            ) : (
              <LoadingWrapper
                isLoaded={summaryOnceLoaded}
                loaderType={LoaderType.PLACEHOLDER}
              >
                {summary?.length > 0 && (
                  <React.Fragment>
                    {summary?.map(skill => (
                      <RatingSummary
                        key={skill.skillTagId}
                        skillTagId={skill.skillTagId}
                        skillRating={skill.avg}
                        totalRatings={skill.totalRatings}
                        submissionId={submissionId}
                        onGetSkillsFeedback={(afterId) => onGetSkillsFeedback(
                          skill.skillTaggingId, afterId,
                        )}
                        skillTaggingId={skill.skillTaggingId}
                        onDeleteFeedback={onDeleteFeedback}
                        canDelete={hasCourseAdmin}
                      />
                    ))}
                    {!isPracticeAdmin && (
                      <div className='text-small gray-3 ml-2 mt-6 '>
                        {t.PRACTICE_ROOM.SKILL_FEEDBACK.FEEDBACK_VISIBILITY.ALL()}
                      </div>
                    )}
                  </React.Fragment>
                )}
              </LoadingWrapper>
            ))}
            {selectedView === SubmissionTab.INSIGHTS && (
              <LoadingWrapper
                loaderType={LoaderType.SPINNER}
                spinnerType={LoadingSpinnerType.CIRCLE}
                isLoaded={transcriptionCompleted}
                className='insights-loader d-flex flex-column align-items-center'
                loadingInfo={(
                  <div className='page-title-small gray-2 mt-6'>
                    {t.PRACTICE_ROOM.INSIGHTS.LOADING()}
                  </div>
                )}
              >
                {transcriptionCompleted && (
                  <div>
                    <div className='gray-1 text-small'>
                      {t.PRACTICE_ROOM.INSIGHTS.DESCRIPTION()}
                    </div>
                    <div className='border-bottom border-gray-4 py-6'>
                      <div className='d-flex'>
                        <InsightsCount
                          count={wordsPerMinute}
                          label={t.PRACTICE_ROOM.INSIGHTS.WPM.LABEL()}
                          className='text-primary'
                          showCount={wordsPerMinute > 0}
                        />
                        <div className='ml-4 d-flex flex-column'>
                          <div className='gray-1 text-regular'>
                            {!rateOfSpeech.length && !wordsPerMinute ? (
                              t.PRACTICE_ROOM.INSIGHTS.WPM.NO_DATA()
                            ) : (
                              <>
                                {t.PRACTICE_ROOM.INSIGHTS.WPM.DESCRIPTION(
                                  configConstants.recordings.wpm.fast.toString(),
                                  configConstants.recordings.wpm.slow.toString(),
                                  !wordsPerMinute ? 'N/A' : wordsPerMinute,
                                )}
                                {rateOfSpeech.length > 1 && ` ${t.PRACTICE_ROOM.INSIGHTS.WPM.CHART()}`}
                              </>
                            )}
                          </div>
                          {rateOfSpeech.length > 1 && (
                            <div className='mt-4'>
                              <NvChart
                                type='line'
                                data={data}
                                options={options}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {keyphrasesToCover?.length > 0 && (
                      <div
                        className={`${keyphrasesToAvoid?.length > 0 ? 'border-bottom border-gray-4' : ''} py-6`}
                      >
                        <div className='d-flex'>
                          <InsightsCount
                            count={coveredKeyphrases?.length}
                            label={t.PRACTICE_ROOM.INSIGHTS.MENTIONED.LABEL()}
                            className={coveredKeyphrases?.length > 0 ? 'text-success' : 'text-warning'}
                          />
                          <div className='ml-4'>
                            <div className='gray-1 text-regular'>
                              {coveredKeyphrases?.length > 0
                                ? t.PRACTICE_ROOM.INSIGHTS.MENTIONED.INCLUDED(coveredKeyphrases?.length)
                                : t.PRACTICE_ROOM.INSIGHTS.MENTIONED.NOT_INCLUDED()}
                            </div>
                            {coveredKeyphrases?.length > 0 && (
                              <PhraseList
                                label={t.PRACTICE_ROOM.INSIGHTS.MENTIONED.MENTIONED()}
                                items={coveredKeyphrases}
                                borderClassName='border-success'
                              />
                            )}
                            {notCoveredKeyphrases?.length > 0 && (
                              <PhraseList
                                label={t.PRACTICE_ROOM.INSIGHTS.MENTIONED.NOT_MENTIONED()}
                                items={notCoveredKeyphrases}
                                borderClassName='border-gray-5'
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {keyphrasesToAvoid?.length > 0 && (
                      <div className='pt-6'>
                        <div className='d-flex'>
                          <InsightsCount
                            count={notAvoidedKeyphrases?.length}
                            label={t.PRACTICE_ROOM.INSIGHTS.AVOIDED.LABEL()}
                            className={notAvoidedKeyphrases?.length > 0 ? 'text-warning' : 'text-success'}
                          />
                          <div className='ml-4'>
                            <div className='gray-1 text-regular'>
                              {notAvoidedKeyphrases?.length === 0
                                ? t.PRACTICE_ROOM.INSIGHTS.AVOIDED.NOT_INCLUDED()
                                : t.PRACTICE_ROOM.INSIGHTS.AVOIDED.INCLUDED(notAvoidedKeyphrases?.length)}
                            </div>
                            {notAvoidedKeyphrases?.length > 0 && (
                              <PhraseList
                                label={t.PRACTICE_ROOM.INSIGHTS.AVOIDED.FORGOT_TO_AVOID()}
                                items={notAvoidedKeyphrases}
                                borderClassName='border-warning'
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </LoadingWrapper>
            )}
          </div>
        )}
      </div>
      {practiceFeedbackActivity && (
        <FeedbackInstructionModal
          title={practiceFeedbackActivity.title}
          setShowModal={setFeedbackInstructionModal}
          showModal={showFeedbackInstructionModal && !!videoRef?.current}
        />
      )}
    </div>
  );
};

export default PracticeSubmission;
